import { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/movies' },
  { label: '资讯', to: '/news' },
  { label: '专题', to: '/specials' },
  { label: '关于', to: '/about' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword('');
    setMenuOpen(false);
  };

  const solid = scrolled || !isHome || menuOpen;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-gradient-to-b from-black/60 to-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="w-9 h-9 rounded-md bg-primary-500 flex items-center justify-center">
              <i className="ri-film-line text-xl text-background-50"></i>
            </span>
            <span className="text-lg md:text-xl font-bold text-foreground-950 whitespace-nowrap">
              午夜<span className="text-primary-500">电影网</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-md text-sm transition-colors ${
                    isActive
                      ? 'text-primary-500 font-medium'
                      : 'text-foreground-700 hover:text-primary-400'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={handleSearch}
              className="hidden md:flex items-center gap-2 bg-background-100 border border-background-300/60 rounded-full px-3 py-1.5 focus-within:border-primary-400 transition-colors"
            >
              <i className="ri-search-line text-foreground-500 text-sm"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索电影 / 资讯"
                className="bg-transparent border-none outline-none text-sm text-foreground-900 placeholder:text-foreground-500 w-36 lg:w-48"
                aria-label="站内搜索"
              />
            </form>

            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="打开菜单"
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-900 hover:bg-background-200/60 transition-colors"
            >
              <i className={menuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'}></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50/98 backdrop-blur-md animate-fade-in">
          <div className="px-4 py-4 flex flex-col gap-1">
            <form onSubmit={handleSearch} className="flex items-center gap-2 bg-background-100 border border-background-300/60 rounded-full px-3 py-2 mb-2">
              <i className="ri-search-line text-foreground-500 text-sm"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索电影 / 资讯"
                className="bg-transparent border-none outline-none text-sm text-foreground-900 placeholder:text-foreground-500 flex-1"
              />
              <button type="submit" className="text-primary-500 text-sm whitespace-nowrap">搜索</button>
            </form>
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-sm ${
                    isActive ? 'bg-primary-500/10 text-primary-400 font-medium' : 'text-foreground-700'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}